import {Box, Button, CloseButton, Grid, HStack, IconButton, Switch, Text} from '@chakra-ui/react';
import {Ellipsis, PanelBottom} from 'lucide-react';
import {CountBadge} from '../../components/CountBadge';
import {ViewerAction} from './useViewerActions';
import {ViewerPanel, ViewerPanelSide, ViewerPanelState} from './viewerPanels';

interface ViewerTopBarProps {
    /** Of the current photo, from 0. */
    index: number;
    total: number;
    fileName: string;
    onCopyName: () => void;
    actions: ViewerAction[];
    originalSwitch: { checked: boolean; disabled: boolean; toggle: () => void };
    panels: ViewerPanel[];
    panelState: ViewerPanelState;
    isMobile: boolean;
    /** Opens the actions menu under the "⋯" button (phones). */
    onOpenMenu: (button: Element) => void;
    onClose: () => void;
}

const ghostOnDark = {variant: 'ghost', color: 'white', _hover: {bg: 'whiteAlpha.200'}} as const;

/**
 * The viewer's top bar, across the whole width: where we are on the left, the actions in the centre,
 * the panel toggles and close on the right. On phones the name moves to the picture's foot, the
 * less used actions into the "⋯" menu, and one toggle opens the sheet with every panel.
 */
export function ViewerTopBar({
    index,
    total,
    fileName,
    onCopyName,
    actions,
    originalSwitch,
    panels,
    panelState,
    isMobile,
    onOpenMenu,
    onClose,
}: ViewerTopBarProps) {
    const barActions = actions.filter((a) => a.bar === 'always' || (a.bar === 'desktop' && !isMobile));
    const panelsOn = (side: ViewerPanelSide) => isMobile ? [] : panels.filter((p) => p.side === side);

    return <Grid
        templateColumns="1fr auto 1fr"
        alignItems="center"
        gap={2}
        px={2}
        h="48px"
        flexShrink={0}
        bg="black"
        color="white"
        borderBottomWidth="1px"
        borderColor="whiteAlpha.300"
    >
        <HStack gap={2} minW={0}>
            {panelsOn('left').map((panel) => <PanelToggle key={panel.id} panel={panel} panelState={panelState}/>)}
            <Text fontSize="sm" color="gray.300" whiteSpace="nowrap" fontVariantNumeric="tabular-nums" px={1}>
                {index + 1} / {total}
            </Text>
            {!isMobile &&
                <Text
                    fontSize="sm"
                    truncate
                    cursor="pointer"
                    onClick={onCopyName}
                    title="Kattints a fájlnév másolásához (N)"
                >
                    {fileName}
                </Text>
            }
        </HStack>

        <HStack gap={1}>
            {barActions.map((action) => <ActionButton key={action.id} action={action}/>)}
            <Switch.Root
                mx={1}
                gap={2}
                display={{base: 'none', sm: 'inline-flex'}}
                size="sm"
                colorPalette="blue"
                checked={originalSwitch.checked}
                onCheckedChange={originalSwitch.toggle}
                disabled={originalSwitch.disabled}
            >
                <Switch.HiddenInput/>
                <Switch.Control/>
                <Switch.Label color="white" fontSize="sm" fontWeight="normal">Eredeti</Switch.Label>
            </Switch.Root>
            {isMobile &&
                <IconButton
                    aria-label="További műveletek"
                    title="További műveletek"
                    size="sm"
                    {...ghostOnDark}
                    onClick={(e) => onOpenMenu(e.currentTarget)}
                >
                    <Ellipsis size={18}/>
                </IconButton>
            }
        </HStack>

        <HStack gap={1} justify="flex-end">
            {panelsOn('right').map((panel) => <PanelToggle key={panel.id} panel={panel} panelState={panelState}/>)}
            {isMobile && panels.length > 0 && <SheetToggle panels={panels} panelState={panelState}/>}
            <CloseButton aria-label="Bezárás" title="Bezárás (Esc)" size="sm" {...ghostOnDark} onClick={onClose}/>
        </HStack>
    </Grid>;
}

function ActionButton({action}: { action: ViewerAction }) {
    const common = {
        'aria-label': action.label,
        title: action.title ?? action.label,
        'aria-pressed': action.pressed,
        size: 'sm',
        ...ghostOnDark,
        color: action.color ?? 'white',
        onClick: action.run,
        loading: action.pending,
        disabled: action.disabled,
    } as const;
    if (action.text) {
        // The text from `md` up; on phones the icon alone, as the other buttons.
        return <Button {...common} px={{base: 0, md: 3}}>
            <Box as="span" display={{base: 'inline', md: 'none'}}>{action.icon}</Box>
            <Box as="span" display={{base: 'none', md: 'inline'}}>{action.text}</Box>
        </Button>;
    }
    return <IconButton {...common}>{action.icon}</IconButton>;
}

/** A toggle with a count badge, solid blue while its panel is shown. */
function ToggleButton({label, title, icon, badge, pressed, onClick}: {
    label: string;
    title?: string;
    icon: React.ReactNode;
    badge?: number;
    pressed: boolean;
    onClick: () => void;
}) {
    return <Box position="relative">
        <IconButton
            aria-label={label}
            title={title ?? label}
            aria-pressed={pressed}
            size="sm"
            variant={pressed ? 'solid' : 'ghost'}
            colorPalette={pressed ? 'blue' : undefined}
            color={pressed ? undefined : 'white'}
            _hover={pressed ? undefined : {bg: 'whiteAlpha.200'}}
            onClick={onClick}
        >
            {icon}
        </IconButton>
        <CountBadge
            count={badge ?? 0}
            max={9}
            size="sm"
            position="absolute"
            top="-3px"
            right="-3px"
            pointerEvents="none"
            boxShadow="0 0 2px 1px {colors.blackAlpha.800}"
        />
    </Box>;
}

function PanelToggle({panel, panelState}: { panel: ViewerPanel; panelState: ViewerPanelState }) {
    return <ToggleButton
        label={panel.toggleLabel}
        title={panel.toggleTitle}
        icon={panel.icon}
        badge={panel.badge}
        pressed={panelState.isShown(panel)}
        onClick={() => panelState.toggle(panel)}
    />;
}

/** The phones' single toggle: a lone panel's own, or a generic one for the sheet of several. */
function SheetToggle({panels, panelState}: { panels: ViewerPanel[]; panelState: ViewerPanelState }) {
    const only = panels.length === 1 ? panels[0] : undefined;
    return <ToggleButton
        label={only?.toggleLabel ?? 'Oldalsáv megjelenítése/elrejtése'}
        title={only?.toggleTitle}
        icon={only?.icon ?? <PanelBottom size={18}/>}
        badge={panels.reduce((sum, p) => sum + (p.badge ?? 0), 0)}
        pressed={panelState.sheetOpen}
        onClick={panelState.toggleSheet}
    />;
}
